import MobileBackButton from "./MobileBackButton";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { FiCheck, FiChevronRight, FiLogIn, FiLogOut } from "react-icons/fi";
import { FaClock } from "react-icons/fa6";

import "./RegularizationForm.css";

/*
 * Presentational attendance regularization screen shared by the user & admin
 * mobile pages. All data / behaviour stays in the page; this only renders it.
 * `state` is the router state sent by the attendance screens (date as DD/MM/YYYY).
 */

// keeps the existing rules: digits only, max 2 chars, within range
const acceptTimePart = (value, max) =>
  value === "" || (/^\d{1,2}$/.test(value) && Number(value) <= max);

const pad2 = (value) => (value === "" ? "" : String(value).padStart(2, "0"));

const SubmittedSheet = ({ day, inTime, outTime, onDone }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog.showModal();
    return () => dialog.close();
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      className="rf-success-sheet"
      aria-labelledby="rf-success-title"
      aria-describedby="rf-success-description"
      onCancel={(event) => { event.preventDefault(); onDone(); }}
    >
      <div className="rf-success-handle" aria-hidden="true" />
      <div className="rf-success-symbol" aria-hidden="true">
        <span className="rf-success-check"><FiCheck /></span>
      </div>
      <h2 id="rf-success-title">Request Submitted</h2>
      <p id="rf-success-description">
        {day?.isValid() ? day.format("dddd, DD MMM YYYY") : ""}
        {" · "}{inTime}–{outTime}
        <br />Sent for approval
      </p>
      <button type="button" className="rf-success-done" onClick={onDone} autoFocus>
        Done!
      </button>
    </dialog>,
    document.body,
  );
};

const TimeBox = ({ label, hour, minute, onHourChange, onMinuteChange }) => (
  <div className="rf-time-box">
    <input
      type="text"
      inputMode="numeric"
      className="rf-time-input"
      aria-label={`${label} hour`}
      placeholder="HH"
      value={hour}
      onChange={(e) => acceptTimePart(e.target.value, 23) && onHourChange(e.target.value)}
      onBlur={() => onHourChange(pad2(hour))}
    />
    <span className="rf-time-sep">:</span>
    <input
      type="text"
      inputMode="numeric"
      className="rf-time-input"
      aria-label={`${label} minute`}
      placeholder="MM"
      value={minute}
      onChange={(e) => acceptTimePart(e.target.value, 59) && onMinuteChange(e.target.value)}
      onBlur={() => onMinuteChange(pad2(minute))}
    />
    <FaClock className="rf-time-clock" />
  </div>
);

const RegularizationForm = ({
  state,
  inHour,
  inMinute,
  outHour,
  outMinute,
  onInHourChange,
  onInMinuteChange,
  onOutHourChange,
  onOutMinuteChange,
  remarks,
  onRemarksChange,
  onApply,
  onBack,
  submitted = false,
  isSubmitting = false,
  onDone,
}) => {
  const day = state?.date ? moment(state.date, "DD/MM/YYYY") : null;

  let shiftLabel = "-";
  if (state?.shiftType === "Weekoff") shiftLabel = "Week Off";
  else if (state?.shiftType === "Holiday") shiftLabel = "Holiday";
  else if (state?.shiftName) {
    shiftLabel = /shift$/i.test(state.shiftName)
      ? state.shiftName
      : `${state.shiftName} Shift`;
  }

  return (
    <div className="rf-page">
      {/* TITLE */}
      <div className="rf-title-row">
        <MobileBackButton type="button" className="rf-back" onClick={onBack} aria-label="Back" />
        <div>
          <p className="rf-title-sub">Regularization</p>
          <h2 className="rf-title">Update Attendance</h2>
        </div>
      </div>

      {!state ? (
        <section className="rf-card rf-empty">
          <p className="rf-empty-title">No Missing Punches</p>
          <p className="rf-empty-text">
            You don't have any missing punches to regularize.
          </p>
          <button type="button" className="rf-apply" onClick={onBack}>
            <span className="rf-apply-text">Back</span>
          </button>
        </section>
      ) : (
        <>
          {/* DATE + SHIFT */}
          <section className="rf-card rf-date-card">
            <div className="rf-date-row">
              <div className="rf-date-badge">
                <span className="rf-date-month">
                  {day?.isValid() ? day.format("MMM").toUpperCase() : "-"}
                </span>
                <span className="rf-date-day">
                  {day?.isValid() ? day.format("DD") : "-"}
                </span>
              </div>
              <div className="rf-date-text">
                <p className="rf-muted">Attendance Date</p>
                <p className="rf-date-full">
                  {day?.isValid() ? day.format("dddd, DD MMM YYYY") : state.date || "-"}
                </p>
              </div>
            </div>

            <div className="rf-divider" />

            <div className="rf-shift-row">
              <span className="rf-shift-label">Shift</span>
              <span className="rf-shift-pill">
                <FaClock />
                {shiftLabel}
              </span>
            </div>
          </section>

          {/* PUNCH TIMINGS */}
          <section className="rf-card rf-punch-card">
            <p className="rf-section-title">Punch Timings</p>

            <div className="rf-punch-row">
              <span className="rf-punch-icon">
                <FiLogIn />
              </span>
              <div className="rf-punch-text">
                <span className="rf-punch-label">Punch-In</span>
                <span className="rf-punch-date">{state.date || "-"}</span>
              </div>
              <TimeBox
                label="Punch-In"
                hour={inHour}
                minute={inMinute}
                onHourChange={onInHourChange}
                onMinuteChange={onInMinuteChange}
              />
            </div>

            <div className="rf-connector" aria-hidden="true" />

            <div className="rf-punch-row">
              <span className="rf-punch-icon">
                <FiLogOut />
              </span>
              <div className="rf-punch-text">
                <span className="rf-punch-label">Punch-Out</span>
                <span className="rf-punch-date">{state.date || "-"}</span>
              </div>
              <TimeBox
                label="Punch-Out"
                hour={outHour}
                minute={outMinute}
                onHourChange={onOutHourChange}
                onMinuteChange={onOutMinuteChange}
              />
            </div>
          </section>

          {/* REMARKS */}
          <label className="rf-remarks">
            <span className="rf-remarks-label">Remarks</span>
            <textarea
              className="rf-card rf-remarks-input"
              rows={3}
              placeholder="Enter the reason for regularization, eg: missed punch at gate."
              value={remarks}
              onChange={(e) => onRemarksChange(e.target.value)}
            />
          </label>

          {/* APPLY */}
          <button type="button" className="rf-apply" onClick={onApply} disabled={isSubmitting || submitted}>
            <span className="rf-apply-bubble">
              <span className="rf-apply-dot" />
            </span>
            <span className="rf-apply-text">{isSubmitting ? "Submitting…" : "Apply for Regularization"}</span>
            <span className="rf-apply-arrow">
              <FiChevronRight />
            </span>
          </button>
        </>
      )}
      {submitted && (
        <SubmittedSheet
          day={day}
          inTime={`${pad2(inHour)}:${pad2(inMinute)}`}
          outTime={`${pad2(outHour)}:${pad2(outMinute)}`}
          onDone={onDone}
        />
      )}
    </div>
  );
};

export default RegularizationForm;
